<script setup lang="ts">
import ItemV3StonesStoneSize from "@/components/itemV3/parameter/stones/parameter/itemV3StonesStoneSize.vue";
import ItemV3StonesPosition from "@/components/itemV3/parameter/stones/parameter/itemV3StonesPosition.vue";
import {inject, reactive, ref} from "vue";
import ItemV3StonesForm from "@/components/itemV3/parameter/stones/parameter/itemV3StonesForm.vue";
import ItemV3StonesWaves from "@/components/itemV3/parameter/stones/parameter/itemV3StonesWaves.vue";
import ItemV3StonesSetting from "@/components/itemV3/parameter/stones/parameter/itemV3StonesSetting.vue";
import ItemV3StonesQuality from "@/components/itemV3/parameter/stones/parameter/itemV3StonesQuality.vue";
import ItemV3StonesNumber from "@/components/itemV3/parameter/stones/parameter/itemV3StonesNumber.vue";
import ItemV3StonesAddons from "@/components/itemV3/parameter/stones/parameter/itemV3StonesAddons.vue";
import ItemV3StonesPresets from "@/components/itemV3/parameter/stones/parameter/itemV3StonesPresets.vue";
import ItemV3StonesFormAngle from "@/components/itemV3/parameter/stones/parameter/itemV3StonesFormAngle.vue";

const props = defineProps<{ groupIdx: number }>();
const emit = defineEmits(["patch"]);

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

const focusSettings = [
  "BEZEL",
  "PAVE",
  "CHANNEL",
  "CROWN1"
]

const focusForm = [
  "ROUND",
  "PRINCESS",
  "BAGUETTE",
  "HEART",
  "OVAL",
  "MARQUISE",
  "PEAR",
  "TRIANGLE"
]

// only these stone_form_angle values (by idx) are shown; an empty list shows all
const focusFormAngle = [
  0,
  90
]

// shown via item-v3-stones-setting's "other" button, which emits "show-presets"
const showPresets = ref(false)

/**
 * headlines - collapsible sections (transferred from itemV3Grooves.vue's
 * "groove details - collapsible sections")
 */

const openSections = reactive<Record<string, boolean>>({
  waves: true,
  setting: true,
  addons: false,
  form: false,
  formAngle: false,
  number: false,
  quality: false,
  stone_size: false,
  position: false,
})

const toggleSection = (key: string) => {
  openSections[key] = !openSections[key]
}


</script>

<template>
  <div class="w-full">

    <div class="w-full">
      <!-- Verlauf -->
      <button
          type="button"
          class="flex w-full items-center gap-3 mb-3 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536]"
          :aria-expanded="openSections.waves"
          @click="toggleSection('waves')"
      >
        <span class="h-px flex-1 bg-[#e7ddcc]"></span>
        <span
            class="flex items-center gap-1.5 text-xs leading-[15px] font-normal tracking-[0.1px] text-parameter-text whitespace-nowrap">
          Verlauf
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none"
               class="size-2.5 shrink-0 text-[#555956] transition-transform"
               :class="openSections.waves ? 'rotate-180' : ''">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="h-px flex-1 bg-[#e7ddcc]"></span>
      </button>
      <item-v3-stones-waves
          v-if="openSections.waves"
          class="w-full"
          :group-idx="props.groupIdx" @patch="(event) => emit('patch', event)"/>
    </div>

    <div class="w-full">
      <!-- Fassung - stones.*.stone_setting -->
      <button
          type="button"
          class="flex w-full items-center gap-3 mb-3 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536]"
          :aria-expanded="openSections.setting"
          @click="toggleSection('setting')"
      >
        <span class="h-px flex-1 bg-[#e7ddcc]"></span>
        <span
            class="flex items-center gap-1.5 text-xs leading-[15px] font-normal tracking-[0.1px] text-parameter-text whitespace-nowrap">
          Fassung
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none"
               class="size-2.5 shrink-0 text-[#555956] transition-transform"
               :class="openSections.setting ? 'rotate-180' : ''">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="h-px flex-1 bg-[#e7ddcc]"></span>
      </button>
      <item-v3-stones-setting
          v-if="openSections.setting"
          class="w-full"
          :prio-settings="focusSettings"
          stone-layout="row"
          :group-idx="props.groupIdx" @patch="(event) => emit('patch', event)" @show-presets="showPresets = !showPresets"/>
    </div>

    <item-v3-stones-presets
        class="w-full px-4"
        :show="showPresets"
        stone-layout="row"
        @patch="(event) => emit('patch', event)"
    />

    <div class="w-full">
      <!-- Zusatzdesign - stones.*.stone_addons -->
      <button
          type="button"
          class="flex w-full items-center gap-3 mb-3 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536]"
          :aria-expanded="openSections.addons"
          @click="toggleSection('addons')"
      >
        <span class="h-px flex-1 bg-[#e7ddcc]"></span>
        <span
            class="flex items-center gap-1.5 text-xs leading-[15px] font-normal tracking-[0.1px] text-parameter-text whitespace-nowrap">
          Fassung-Varianten
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none"
               class="size-2.5 shrink-0 text-[#555956] transition-transform"
               :class="openSections.addons ? 'rotate-180' : ''">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="h-px flex-1 bg-[#e7ddcc]"></span>
      </button>
      <item-v3-stones-addons
          v-if="openSections.addons"
          class="w-full"
          stone-layout="row"
          :group-idx="props.groupIdx" @patch="(event) => emit('patch', event)"/>
    </div>

    <div class="w-full">
      <!-- Form - stones.*.stone_form -->
      <button
          type="button"
          class="flex w-full items-center gap-3 mb-3 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536]"
          :aria-expanded="openSections.form"
          @click="toggleSection('form')"
      >
        <span class="h-px flex-1 bg-[#e7ddcc]"></span>
        <span
            class="flex items-center gap-1.5 text-xs leading-[15px] font-normal tracking-[0.1px] text-parameter-text whitespace-nowrap">
          Form
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none"
               class="size-2.5 shrink-0 text-[#555956] transition-transform"
               :class="openSections.form ? 'rotate-180' : ''">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="h-px flex-1 bg-[#e7ddcc]"></span>
      </button>
      <item-v3-stones-form
          v-if="openSections.form"
          class="w-full"
          :focus-form="focusForm"
          :group-idx="props.groupIdx" @patch="(event) => emit('patch', event)"/>
    </div>

    <div class="w-full">
      <!-- Form - stones.*.stone_form_angle -->
      <button
          type="button"
          class="flex w-full items-center gap-3 mb-3 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536]"
          :aria-expanded="openSections.formAngle"
          @click="toggleSection('formAngle')"
      >
        <span class="h-px flex-1 bg-[#e7ddcc]"></span>
        <span
            class="flex items-center gap-1.5 text-xs leading-[15px] font-normal tracking-[0.1px] text-parameter-text whitespace-nowrap">
          Steinwinkel
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none"
               class="size-2.5 shrink-0 text-[#555956] transition-transform"
               :class="openSections.formAngle ? 'rotate-180' : ''">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="h-px flex-1 bg-[#e7ddcc]"></span>
      </button>
      <item-v3-stones-form-angle
          v-if="openSections.formAngle"
          class="w-full"
          :prio-settings="focusFormAngle"
          :group-idx="props.groupIdx" @patch="(event) => emit('patch', event)"/>
    </div>

    <div class="w-full">
      <!-- stone number and spreading -->
      <button
          type="button"
          class="flex w-full items-center gap-3 mb-3 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536]"
          :aria-expanded="openSections.number"
          @click="toggleSection('number')"
      >
        <span class="h-px flex-1 bg-[#e7ddcc]"></span>
        <span
            class="flex items-center gap-1.5 text-xs leading-[15px] font-normal tracking-[0.1px] text-parameter-text whitespace-nowrap">
          {{ $t('components.parameters.stones.number.title') }}
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none"
               class="size-2.5 shrink-0 text-[#555956] transition-transform"
               :class="openSections.number ? 'rotate-180' : ''">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="h-px flex-1 bg-[#e7ddcc]"></span>
      </button>
      <item-v3-stones-number
          v-if="openSections.number"
          class="w-full"
          :group-idx="props.groupIdx" @patch="(event) => emit('patch', event)"/>
    </div>

    <div class="w-full">
      <!-- Steinart/Qualität - stones.*.stone_type/stone_quality -->
      <button
          type="button"
          class="flex w-full items-center gap-3 mb-3 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536]"
          :aria-expanded="openSections.quality"
          @click="toggleSection('quality')"
      >
        <span class="h-px flex-1 bg-[#e7ddcc]"></span>
        <span
            class="flex items-center gap-1.5 text-xs leading-[15px] font-normal tracking-[0.1px] text-parameter-text whitespace-nowrap">
          Steinart/Qualität
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none"
               class="size-2.5 shrink-0 text-[#555956] transition-transform"
               :class="openSections.quality ? 'rotate-180' : ''">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="h-px flex-1 bg-[#e7ddcc]"></span>
      </button>
      <item-v3-stones-quality
          v-if="openSections.quality"
          class="w-full"
          :group-idx="props.groupIdx" @patch="(event) => emit('patch', event)"/>
    </div>

    <div class="w-full flex divide-x divide-[#e7ddcc]">
      <div class="w-1/2 px-4 first:pl-0 last:pr-0">
        <!-- steingröße - stones.*.stone_size -->
        <button
            type="button"
            class="flex w-full items-center gap-3 mb-3 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536]"
            :aria-expanded="openSections.stone_size"
            @click="toggleSection('stone_size')"
        >
          <span class="h-px flex-1 bg-[#e7ddcc]"></span>
          <span
              class="flex items-center gap-1.5 text-xs leading-[15px] font-normal tracking-[0.1px] text-parameter-text whitespace-nowrap">
            Steingröße
            <svg aria-hidden="true" viewBox="0 0 12 12" fill="none"
                 class="size-2.5 shrink-0 text-[#555956] transition-transform"
                 :class="openSections.stone_size ? 'rotate-180' : ''">
              <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span class="h-px flex-1 bg-[#e7ddcc]"></span>
        </button>
        <item-v3-stones-stone-size
            v-if="openSections.stone_size"
            class="w-full"
            :group-idx="props.groupIdx" @patch="(event) => emit('patch', event)"/>
      </div>
      <div class="w-1/2 px-4 first:pl-0 last:pr-0">
        <!-- Position - stones.*.object and stones.*.object_arrangement -->
        <button
            type="button"
            class="flex w-full items-center gap-3 mb-3 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536]"
            :aria-expanded="openSections.position"
            @click="toggleSection('position')"
        >
          <span class="h-px flex-1 bg-[#e7ddcc]"></span>
          <span
              class="flex items-center gap-1.5 text-xs leading-[15px] font-normal tracking-[0.1px] text-parameter-text whitespace-nowrap">
            Position
            <svg aria-hidden="true" viewBox="0 0 12 12" fill="none"
                 class="size-2.5 shrink-0 text-[#555956] transition-transform"
                 :class="openSections.position ? 'rotate-180' : ''">
              <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span class="h-px flex-1 bg-[#e7ddcc]"></span>
        </button>
        <item-v3-stones-position
            v-if="openSections.position"
            class="w-full"
            :group-idx="props.groupIdx" @patch="(event) => emit('patch', event)"/>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>
